import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, Plus, X, Check } from 'lucide-react';
import SectionShell from './SectionShell';
import { jobBriefApi } from '../../../services/jobBriefApi';

/**
 * HazardsSection — Add / Dismiss / N-A workflow.
 *
 * Each hazard row is:  {category, label, note, status}
 * status ∈ {'active', 'dismissed', 'not_applicable'}
 *
 * Categories come from `/catalogs/hazards`; users can also type a custom
 * category (free-form). Dismissing preserves audit history — we never
 * silently delete a reviewed hazard.
 */
export default function HazardsSection({ draft, onChange }) {
  const hazards = draft.hazards || [];
  const [catalog, setCatalog] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const [customCat, setCustomCat] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    jobBriefApi.getHazardCatalog().then((d) => setCatalog(d?.hazards || []));
  }, []);

  const setStatus = useCallback((idx, status) => {
    const next = hazards.slice();
    next[idx] = { ...next[idx], status };
    onChange({ hazards: next });
  }, [hazards, onChange]);

  const removeAt = useCallback((idx) => {
    const next = hazards.slice();
    next.splice(idx, 1);
    onChange({ hazards: next });
  }, [hazards, onChange]);

  const addHazard = useCallback(() => {
    const category = (customCat || selectedCat || '').trim();
    if (!category) return;
    const catRow = catalog.find((c) => c.category === category);
    const row = {
      category,
      label: catRow?.label || category,
      note: note.trim(),
      status: 'active',
    };
    onChange({ hazards: [...hazards, row] });
    setSelectedCat('');
    setCustomCat('');
    setNote('');
  }, [catalog, customCat, selectedCat, note, hazards, onChange]);

  return (
    <SectionShell
      icon={ShieldAlert}
      title="Hazard Identification"
      subtitle="Review, dismiss with reason, or add. Every hazard you interact with is captured in the audit log."
      color="#fb7185"
      badge={hazards.length ? `${hazards.filter((h) => h.status === 'active' || !h.status).length} active` : 'Review'}
      testId="jbv2-section-hazards"
    >
      {/* Existing hazard rows */}
      {hazards.length === 0 && (
        <div className="jbv2-empty" data-testid="jbv2-hazards-empty">
          No hazards reviewed yet. Add the first hazard below — or confirm N-A for each so the audit log shows you reviewed them.
        </div>
      )}
      {hazards.map((h, i) => (
        <div
          key={i}
          className={`jbv2-row${h.status === 'dismissed' ? ' jbv2-row--dismissed' : ''}`}
          data-testid={`jbv2-hazard-row-${i}`}
        >
          <div className="jbv2-row-body">
            <div className="jbv2-row-cat">{h.label || h.category}</div>
            {h.note && <div className="jbv2-row-note">{h.note}</div>}
            {h.status === 'not_applicable' && <div className="jbv2-row-note" style={{ color: '#94a3b8' }}>Marked N-A.</div>}
          </div>
          <div className="jbv2-row-actions">
            {(!h.status || h.status === 'active') && (
              <button
                type="button"
                className="jbv2-row-btn jbv2-row-btn--muted"
                onClick={() => setStatus(i, 'not_applicable')}
                data-testid={`jbv2-hazard-na-${i}`}
                aria-label="Mark not applicable"
              >
                N-A
              </button>
            )}
            {(!h.status || h.status === 'active') && (
              <button
                type="button"
                className="jbv2-row-btn"
                onClick={() => setStatus(i, 'dismissed')}
                data-testid={`jbv2-hazard-dismiss-${i}`}
                aria-label="Dismiss hazard"
              >
                Dismiss
              </button>
            )}
            {(h.status === 'dismissed' || h.status === 'not_applicable') && (
              <button
                type="button"
                className="jbv2-row-btn"
                onClick={() => setStatus(i, 'active')}
                data-testid={`jbv2-hazard-reactivate-${i}`}
                aria-label="Reactivate"
              >
                <Check size={12} /> Restore
              </button>
            )}
            <button
              type="button"
              className="jbv2-row-btn jbv2-row-btn--danger"
              onClick={() => removeAt(i)}
              data-testid={`jbv2-hazard-remove-${i}`}
              aria-label="Remove hazard"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      ))}

      {/* Add-hazard control */}
      <div className="jbv2-add-row" data-testid="jbv2-hazard-add-row">
        <select
          value={selectedCat}
          onChange={(e) => setSelectedCat(e.target.value)}
          data-testid="jbv2-hazard-add-category"
          aria-label="Hazard category"
        >
          <option value="">Category…</option>
          {catalog.map((c) => (
            <option key={c.category} value={c.category}>{c.label}</option>
          ))}
        </select>
        <input
          type="text"
          value={customCat}
          onChange={(e) => setCustomCat(e.target.value)}
          placeholder="…or custom hazard"
          data-testid="jbv2-hazard-add-custom"
          aria-label="Custom hazard"
        />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (why is it a hazard here?)"
          data-testid="jbv2-hazard-add-note"
          aria-label="Hazard note"
        />
        <button
          type="button"
          onClick={addHazard}
          disabled={!selectedCat && !customCat.trim()}
          data-testid="jbv2-hazard-add-btn"
        >
          <Plus size={13} /> Add hazard
        </button>
      </div>
    </SectionShell>
  );
}
